"use client";

import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Box,
  Typography,
  Select,
  MenuItem,
  TextField,
  Chip,
  LinearProgress,
  FormControl,
  InputLabel,
  Pagination,
} from "@mui/material";
import { ILeaderBoard } from "@/api";
import { GridColDef } from "@mui/x-data-grid";
import { StyledLink } from "@/components/StyledLink";
import ListingTableTemplate from "@/components/ListingTableTemplate";
import { useUser } from "@/contexts/UserContext";
import { useNavigate } from "react-router";

interface DistributorDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  initialData?: ILeaderBoardExtend;
}

interface RowData {
  id: number;
  rank: number;
  username: string;
  total_score: number;
  effort_score: number;
  performance_score: number;
  challenge_score: number;
  total_activity: number;
  count_activity: number;
}

export interface ILeaderBoardExtend extends ILeaderBoard {}

export default function LeaderboardDialog({
  open,
  setOpen,
  initialData,
}: DistributorDialogProps) {
  const [rows, setRows] = useState<RowData[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentRank, setCurrentRank] = useState("N/A");
  const [positionRank, setPositionRank] = useState("");
  const { user } = useUser();
  const navigate = useNavigate();

  const listLevelUser = initialData?.listLevelUser || [];
  const maxLevelId = Math.max(...listLevelUser);
  const contestData = initialData?.contest?.data || [];
  const digitalLevel = initialData?.contest?.digitalLevel || [];
  const [selectedLevel, setSelectedLevel] = useState<number | null>(null);

  useEffect(() => {
    // console.log(initialData);
    if (initialData) {
      if (maxLevelId && selectedLevel === null) {
        setSelectedLevel(maxLevelId);
      }
      // const parsedData = parseRankData(initialData?.rankData ?? []);
      if (initialData?.rankData) {
        const filteredData = parseRankData(
          initialData.rankData.filter(
            (item) => selectedLevel === null || item.level_id === selectedLevel
          )
        );
        setRows(filteredData);
        const currentUserRank = initialData?.rankData.find(
          (item) => item.username == user?.user_login
        )?.rank;
        setCurrentRank(currentUserRank?.toString() || "N/A");
        var positionsAway = Number(currentUserRank) - 1;
        if (positionsAway > 0) {
          setPositionRank(`You're ${positionsAway} positions away from the top.`);
        } else {
          setPositionRank(`You're at the top!`);
        }
      } else {
        setRows([]); // Gán rỗng nếu không có dữ liệu
        setCurrentRank("N/A");
        setPositionRank("");
      }
    }
  }, [initialData, selectedLevel]);

  // Hàm chuyển đổi dữ liệu
  const parseRankData = (rankData: any[]) => {
    return rankData.map((item) => ({
      id: item.rank, // Đặt `id` cho mỗi hàng
      rank: item.rank,
      username: item.username || "N/A", // Giá trị mặc định nếu không có username
      total_score: item.total_score || 0,
      effort_score: item.effort_score || 0,
      performance_score: item.performance_score || 0,
      challenge_score: item.challenge_score || 0,
      total_activity: item.total_activity || 0,
      count_activity: item.count_activity || 0,
      highlight: user?.user_login == item.username ? 1 : 0
    }));
  };

  const columns: GridColDef[] = [
    { field: "rank", headerName: "Rank", flex: 1 },
    { field: "username", headerName: "Username", flex: 1 },
    { field: "total_score", headerName: "Total Score", flex: 1 },
    { field: "effort_score", headerName: "Effort Score", flex: 1 },
    { field: "performance_score", headerName: "Performance Score", flex: 1 },
    { field: "challenge_score", headerName: "Challenge Score", flex: 1 },
    {
      field: "progress",
      headerName: "Progress",
      flex: 1,
      renderCell: (params) => {
        const { total_activity, count_activity } = params.row || {};
        const percentage =
          total_activity > 0 ? (count_activity / total_activity) * 100 : 0;

        return (
          <Box sx={{ position: "relative", width: "100%" }}>
            <LinearProgress
              variant="determinate"
              value={percentage}
              sx={{
                height: "1rem",
                borderRadius: 4,
                bgcolor: "rgb(237, 242, 247)",
                "& .MuiLinearProgress-bar": {
                  bgcolor: "rgb(82, 196, 26)",
                },
              }}
            />
            <Typography
              variant="body2"
              sx={{
                position: "absolute",
                top: 0,
                left: "50%",
                transform: "translateX(-50%)",
                fontWeight: "bold",
                color: "text.secondary",
              }}
            >
              {Math.round(percentage)}%
            </Typography>
          </Box>
        );
      },
    },
  ];

  const [highlightedRow, setHighlightedRow] = useState<number | null>(null);
  const [paginationModel, setPaginationModel] = useState<{
    page: number;
    pageSize: number;
  }>({ page: 0, pageSize: 10 });

  const highlightRow = () => {
    const rowIndex = rows.findIndex((row) => row.username === user?.user_login);
    if (rowIndex !== -1) {
      setHighlightedRow(rowIndex);
      // Tính toán và chuyển trang
      const pageIndex = Math.floor(rowIndex / paginationModel.pageSize); // Giả sử mỗi trang có paginationModel.pageSize dòng
      setPaginationModel((prev) => ({ ...prev, page: pageIndex })); // Cập nhật trang

      // Thực hiện xóa hiệu ứng highlight sau 3 giây
      // setTimeout(() => {
      //     setHighlightedRow(null); // Xóa highlight sau khi 3 giây
      // }, 3000);
    }
  };

  return (
    <Dialog open={open} onClose={() => setOpen(false)} maxWidth="lg" fullWidth>
      {initialData && initialData?.errorType != "" ? (
        <DialogContent sx={{ px: 3, py: 2 }}>
          {initialData.errorType === "school" && (
            <Box>
              <Typography variant="h5" gutterBottom>
                It looks like you're not yet associated with a school
              </Typography>
              <Typography>Next Steps:</Typography>
              <ul>
                <li>Contact your school administrator to get added.</li>
                <li>
                  If you're not part of a school yet, explore our resources and
                  learn how to get started!
                </li>
              </ul>
            </Box>
          )}
          {initialData.errorType === "organization" && (
            <Box>
              <Typography variant="h5" gutterBottom>
                It looks like your school is not yet associated with a contest
                organization.
              </Typography>
              <Typography>Next Steps:</Typography>
              <ul>
                <li>Contact Pythaverse administrator to get added.</li>
              </ul>
            </Box>
          )}
        </DialogContent>
      ) : (
        <>
          <DialogTitle sx={{ px: 3, pt: 3, pb: 2 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 3,
              }}
            >
              <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
                {digitalLevel
                  .filter((digital) => listLevelUser.includes(digital.id))
                  .map((digital) => (
                    <Button
                      key={digital.id}
                      variant={
                        selectedLevel == digital.id ? "contained" : "outlined"
                      }
                      color="primary"
                      onClick={() => setSelectedLevel(digital.id)}
                    >
                      {digital.name}
                    </Button>
                  ))}
              </Box>
              <Button
                variant="contained"
                color="primary"
                sx={{
                  textTransform: "none",
                  borderRadius: 1,
                }}
                onClick={highlightRow}
              >
                Jump to My Rank
              </Button>
            </Box>
          </DialogTitle>

          <DialogContent sx={{ px: 3, py: 2 }}>
            <ListingTableTemplate
              buttonOptions={[]}
              pageTitle=""
              columns={columns}
              rows={rows}
              loading={loading}
              onMenuClick={() => {
                console.log(123);
              }}
              sortModel={[{ field: "name", sort: "asc" }]}
              selectedRow={highlightedRow}
              paginationModel={paginationModel}
              setPaginationModel={setPaginationModel}
            />

            <Box sx={{ display: "flex", gap: 4, mb: 3, mt: 2 }}>
              <Paper variant="outlined" sx={{ flex: 1, p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Your Performance
                </Typography>
                <Typography variant="subtitle1" gutterBottom>
                  Current Rank: {currentRank}
                </Typography>
                <Typography variant="body2" color="success.main">
                  {positionRank}
                </Typography>
              </Paper>

              <Paper variant="outlined" sx={{ flex: 1, p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Improvement Suggestions:
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Focus on improving your Performance Score to advance in rank.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Complete more challenges to boost your Challenge Score.
                </Typography>
              </Paper>
            </Box>

            <Button
              variant="contained"
              color="inherit"
              sx={{
                bgcolor: "black",
                color: "white",
                "&:hover": {
                  bgcolor: "rgb(45, 45, 45)",
                },
                mr: 1,
              }}
              onClick={()=>{navigate('../my-contests')}}
            >
              View Scoring Details
            </Button>
            <Button
              variant="contained"
              color="inherit"
              onClick={() => setOpen(false)}
              sx={{
                bgcolor: "rgb(108, 117, 125)",
                color: "white",
                "&:hover": {
                  bgcolor: "rgb(90, 98, 104)",
                },
              }}
            >
              Close
            </Button>
          </DialogContent>
        </>
      )}
    </Dialog>
  );
}
